/**
 * Terms-acceptance client — records clickwrap acceptance on the server ledger (/rest/terms).
 * The server stamps the document VERSION itself; we only say which documents were agreed to.
 */
import axios from 'axios';
import { getAccessToken } from './authClient.ts';
import { apiBaseUrl } from './apiBase.ts';

/** See server/src/shared/legal/legalVersions.ts's LegalDocument. */
export type LegalDocument = 'consumer_terms' | 'partner_terms' | 'privacy_policy';

export type AcceptTermsContext = 'signup' | 'partner_onboarding' | 'reacceptance';

export interface AcceptTermsResponse {
  ok: boolean;
  accepted: { document: string; version: string }[];
}

export interface TermsStatusResponse {
  /** Map of document key -> current published version string. */
  current: Record<string, string>;
  /** Map of document key -> { version, acceptedAt }, only for documents the caller has accepted. */
  accepted: Partial<Record<string, { version: string; acceptedAt: string }>>;
}

function getApiBaseUrl(): string {
  // One source for this — see services/apiBase.ts for why eight copies was a problem.
  return apiBaseUrl();
}

async function authHeaders(): Promise<{ Authorization: string; 'Content-Type': string }> {
  const token = await getAccessToken();
  if (!token) throw new Error('Not signed in');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

/** Record acceptance of the named documents at their current versions. */
export async function acceptTerms(documents: LegalDocument[], context: AcceptTermsContext = 'signup'): Promise<AcceptTermsResponse> {
  const headers = await authHeaders();
  const res = await axios.post(
    `${getApiBaseUrl()}/rest/terms/accept`,
    { documents, context },
    { headers, timeout: 20_000 },
  );
  return res.data;
}

/** Which documents/versions the signed-in user has accepted vs the current versions. */
export async function getTermsStatus(): Promise<TermsStatusResponse> {
  const headers = await authHeaders();
  const res = await axios.get(`${getApiBaseUrl()}/rest/terms/status`, { headers, timeout: 20_000 });
  return res.data;
}
